import { useCallback, useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ArrowLeft, Building2, Clock3, CreditCard, KeyRound, Link2, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth.hook";
import { useStartupContext } from "@/hooks/useStartupContext";
import { useCompleteStartupData, useUpdateStartup } from "@/hooks/useFinancialData";
import { AppHeader } from "@/components/layout/AppHeader";
import { PendingInvitesBanner } from "@/components/molecules";
import { PageLoader } from "@/components/layout/PageLoader";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { SiteFooter } from "@/components/layout/SiteFooter";
import type { StartupSettingsInitialData } from "@/components/molecules/StartupSettingsForm/StartupSettingsForm";
import type { UpdateStartupData } from "@/types/startup.types";

export interface SettingsOutletContext {
  settingsInitialData?: StartupSettingsInitialData;
  isSaving: boolean;
  isStartupOwner: boolean;
  startupId: string;
  ownerId?: string;
  onSave: (data: UpdateStartupData | Record<string, unknown>) => Promise<void>;
  setFeedback: (feedback: {
    type: "success" | "error";
    message: string;
  } | null) => void;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2 rounded-[14px] px-3 py-2.5 text-sm font-semibold transition-colors ${
    isActive
      ? "bg-brand/15 text-brand ring-1 ring-brand/30"
      : "text-white/70 hover:bg-surface/10 hover:text-white"
  }`;

export function buildSettingsInitialData(
  startupData: Record<string, unknown> | undefined,
): StartupSettingsInitialData | undefined {
  if (!startupData) return undefined;

  const teamSettings = startupData.teamSettings as
    | UpdateStartupData["teamSettings"]
    | undefined;

  return {
    name: startupData.name as string | undefined,
    description: startupData.description as string | undefined,
    initialFunding:
      (startupData.initialFunding as number | undefined) ??
      (startupData.initialBalance as number | undefined),
    industry: startupData.industry as string | undefined,
    currency: (startupData.currency as string | undefined) ?? "USD",
    legalName: startupData.legalName as string | undefined,
    tagline: startupData.tagline as string | undefined,
    website: startupData.website as string | undefined,
    phone: startupData.phone as string | undefined,
    address: startupData.address as StartupSettingsInitialData["address"],
    investorContact: startupData.investorContact as StartupSettingsInitialData["investorContact"],
    startDate:
      (startupData.startDate as string | Date | undefined) ??
      (startupData.foundedDate as string | Date | undefined),
    endDate: startupData.endDate as string | Date | undefined,
    teamSettings: teamSettings
      ? {
          allowInvites: teamSettings.allowInvites ?? true,
          requireApproval: teamSettings.requireApproval ?? false,
        }
      : undefined,
  };
}

function SettingsSidebar({
  startupName,
  userEmail,
}: {
  startupName?: string;
  userEmail?: string;
}) {
  return (
    <aside className="lg:w-64 lg:shrink-0">
      <div className="rounded-[20px] bg-surface-elevated p-4 ring-1 ring-white/[0.09] lg:sticky lg:top-24">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm font-semibold text-white/55 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </Link>

        <div className="mt-5 border-t border-white/10 pt-5">
          <div className="text-[11px] font-black uppercase tracking-wide text-white/45">
            Settings
          </div>
          {startupName ? (
            <div className="mt-1 truncate text-sm font-bold text-white">
              {startupName}
            </div>
          ) : null}
          {userEmail ? (
            <div className="mt-0.5 truncate text-xs text-white/45">{userEmail}</div>
          ) : null}
        </div>

        <nav className="mt-4 space-y-1">
          <NavLink to="/settings/startup" className={navLinkClass}>
            <Building2 className="h-4 w-4 shrink-0" />
            Startup profile
          </NavLink>
          <NavLink to="/settings/timeline" className={navLinkClass}>
            <Clock3 className="h-4 w-4 shrink-0" />
            Timeline
          </NavLink>
          <NavLink to="/settings/team" className={navLinkClass}>
            <Users className="h-4 w-4 shrink-0" />
            Team & sharing
          </NavLink>
          <NavLink to="/settings/investor" className={navLinkClass}>
            <Link2 className="h-4 w-4 shrink-0" />
            Investor portal
          </NavLink>
          <NavLink to="/settings/billing" className={navLinkClass}>
            <CreditCard className="h-4 w-4 shrink-0" />
            Billing
          </NavLink>
          <NavLink to="/settings/api" className={navLinkClass}>
            <KeyRound className="h-4 w-4 shrink-0" />
            API access
          </NavLink>
        </nav>
      </div>
    </aside>
  );
}

function SettingsFeedback({
  message,
  type,
}: {
  message: string | null;
  type: "success" | "error";
}) {
  if (!message) return null;

  return (
    <div
      className={`rounded-[14px] px-4 py-3 text-sm ${
        type === "success"
          ? "border border-brand/30 bg-brand/10 text-brand"
          : "border border-danger/30 bg-danger/10 text-danger"
      }`}
    >
      {message}
    </div>
  );
}

export function SettingsLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, firebaseUser, signOut } = useAuth();
  const { currentStartup, openCreateStartupModal } = useStartupContext();
  const startupId = currentStartup?.id ?? "";
  const { data: startupData, isLoading } = useCompleteStartupData(startupId);
  const updateStartupMutation = useUpdateStartup(startupId);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const settingsInitialData = useMemo(
    () => buildSettingsInitialData(startupData as Record<string, unknown> | undefined),
    [startupData],
  );

  const isStartupOwner = useMemo(() => {
    const ownerId =
      currentStartup?.ownerId ??
      (startupData as { ownerId?: string; userId?: string } | undefined)?.ownerId ??
      (startupData as { userId?: string } | undefined)?.userId;
    const userId = firebaseUser?.uid ?? userProfile?.id;
    if (!userId) return false;
    if (ownerId && ownerId === userId) return true;

    const members =
      (startupData as { members?: Array<{ userId: string; role?: string }> } | undefined)
        ?.members ?? currentStartup?.members;
    return members?.some(
      (member) => member.userId === userId && member.role === "owner",
    ) ?? false;
  }, [
    currentStartup?.ownerId,
    currentStartup?.members,
    startupData,
    firebaseUser?.uid,
    userProfile?.id,
  ]);

  const ownerId = useMemo(() => {
    return (
      currentStartup?.ownerId ??
      (startupData as { ownerId?: string; userId?: string } | undefined)?.ownerId ??
      (startupData as { userId?: string } | undefined)?.userId
    );
  }, [currentStartup?.ownerId, startupData]);

  const handleSave = useCallback(
    async (data: UpdateStartupData | Record<string, unknown>) => {
      setFeedback(null);
      try {
        await updateStartupMutation.mutateAsync(data as UpdateStartupData);
        setFeedback({ type: "success", message: "Settings saved." });
      } catch (error) {
        console.error("Failed to update startup settings:", error);
        setFeedback({
          type: "error",
          message: "Could not save settings. Please try again.",
        });
      }
    },
    [updateStartupMutation],
  );

  const outletContext = useMemo(
    (): SettingsOutletContext => ({
      settingsInitialData,
      isSaving: updateStartupMutation.isPending,
      isStartupOwner,
      startupId,
      ownerId,
      onSave: handleSave,
      setFeedback,
    }),
    [
      settingsInitialData,
      updateStartupMutation.isPending,
      isStartupOwner,
      startupId,
      ownerId,
      handleSave,
    ],
  );

  if (location.pathname === "/settings" || location.pathname === "/settings/") {
    return <Navigate to="/settings/startup" replace />;
  }

  if (!currentStartup || !startupId) {
    return (
      <div className="flex min-h-screen flex-col bg-page text-white">
        <AppHeader
          displayName={userProfile?.displayName}
          onSettingsClick={() => navigate("/settings/startup")}
          onSignOut={signOut}
        />
        <div className="mx-auto max-w-3xl flex-1 px-4 py-16 sm:px-6 lg:px-8">
          <SettingsSectionCard
            title="No startup selected"
            description="Create a startup or choose one from the header before opening settings."
          >
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openCreateStartupModal}
                className="inline-flex rounded-[14px] bg-brand px-4 py-2 text-sm font-semibold text-surface"
              >
                Create startup
              </button>
              <Link
                to="/dashboard"
                className="inline-flex rounded-[14px] border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 hover:bg-surface/10"
              >
                Go to dashboard
              </Link>
            </div>
          </SettingsSectionCard>
        </div>
        <SiteFooter variant="compact" />
      </div>
    );
  }

  if (isLoading && !startupData) {
    return <PageLoader />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-page text-white">
      <AppHeader
        displayName={userProfile?.displayName}
        onSettingsClick={() => navigate("/settings/startup")}
        onSignOut={signOut}
      />
      <PendingInvitesBanner />

      <div className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-black text-white">Settings</h1>
          <p className="mt-1 text-sm text-white/60">
            Manage your startup profile, timeline, team access, and API keys.
          </p>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
          <SettingsSidebar
            startupName={currentStartup.name}
            userEmail={userProfile?.email}
          />

          <main className="min-w-0 flex-1 space-y-6">
            <SettingsFeedback
              message={feedback?.message ?? null}
              type={feedback?.type ?? "success"}
            />
            <Outlet context={outletContext} />
          </main>
        </div>
      </div>

      <SiteFooter variant="compact" className="mt-auto" />
    </div>
  );
}
